/**
 * The header block and the financial summary as the recipe sets them: one
 * policy per field, saved the moment it is chosen.
 *
 * A block group is ONE row ("Transaction type or raise size — either one
 * unblocks the memo"), because that is one decision: its policy is written to
 * every member so they cannot drift apart. Derived figures show "code" instead
 * of a policy — they are computed from the rows above and cannot be missing on
 * their own.
 *
 * There is deliberately no "looks in" column: the run reads the whole folder,
 * and a per-field document list would promise a search order nothing honours.
 *
 * The financial summary lists the recipe's default figures only (DOP-574).
 * The figures a holding company's memo switches on for itself (NAV per share,
 * EPS, share price…) are not defaults a firm sets for every deal.
 */

import React from "react";

import type {
  DealScreeningFigureSpec,
  DealScreeningHeaderField,
  DealScreeningPolicy,
  DealScreeningRecipeEdits,
} from "../../types/dealScreening";

/**
 * The policies in the words a banker uses (DOP-620). "Must have" is the old
 * "Blocks memo": the memo cannot be marked reviewed without it.
 */
export const POLICY_LABEL: Record<DealScreeningPolicy, string> = {
  blocks: "Must have",
  required: "Should have",
  optional: "Nice to have",
};

/** What each policy does when the fact is missing, one line each (DOP-660). */
export const POLICY_MEANING: ReadonlyArray<
  readonly [DealScreeningPolicy, string]
> = [
  [
    "blocks",
    "has to be found or answered before the memo can be marked reviewed.",
  ],
  [
    "required",
    "a missing one is listed as a gap, with a question for the company, but does not hold up review.",
  ],
  ["optional", "a missing one is not listed as a gap."],
];

const POLICIES: DealScreeningPolicy[] = ["blocks", "required", "optional"];

interface HeaderRow {
  id: string;
  label: string;
  note: string | null;
  fields: DealScreeningHeaderField[];
}

const lowerFirst = (value: string): string =>
  value.charAt(0).toLowerCase() + value.slice(1);

/** Header fields in order, a block group folded into one row. */
export function headerRows(fields: DealScreeningHeaderField[]): HeaderRow[] {
  const rows: HeaderRow[] = [];
  const byGroup = new Map<string, HeaderRow>();
  for (const field of [...fields].sort((a, b) => a.sort_order - b.sort_order)) {
    const existing = field.block_group
      ? byGroup.get(field.block_group)
      : undefined;
    if (existing) {
      existing.fields.push(field);
      existing.label = `${existing.label} or ${lowerFirst(field.label)}`;
      continue;
    }
    const row: HeaderRow = {
      id: field.block_group ? `group:${field.block_group}` : field.key,
      label: field.label,
      note: field.block_group ? "either one is enough" : null,
      fields: [field],
    };
    if (field.block_group) byGroup.set(field.block_group, row);
    rows.push(row);
  }
  return rows;
}

export interface RecipePolicyTablesProps {
  header: DealScreeningHeaderField[];
  figures: DealScreeningFigureSpec[];
  /** Keys whose save is in flight; their selects wait. */
  savingKeys: ReadonlySet<string>;
  onSave: (edits: DealScreeningRecipeEdits, keys: string[]) => void;
  /** Settings that belong with the figures, shown above the rows. */
  figuresExtra?: React.ReactNode;
}

const SELECT =
  "min-w-[9.5rem] rounded-lg border border-gray-300 py-1.5 pl-2.5 pr-8 text-sm text-text-base focus:outline-none focus:ring-2 focus:ring-[#008782] disabled:opacity-60";

interface PolicySelectProps {
  label: string;
  value: DealScreeningPolicy;
  disabled: boolean;
  onChange: (policy: DealScreeningPolicy) => void;
}

const PolicySelect: React.FC<PolicySelectProps> = ({
  label,
  value,
  disabled,
  onChange,
}) => (
  <select
    aria-label={`Policy for ${label}`}
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value as DealScreeningPolicy)}
    className={SELECT}
  >
    {POLICIES.map((policy) => (
      <option key={policy} value={policy}>
        {POLICY_LABEL[policy]}
      </option>
    ))}
  </select>
);

interface PolicyCardProps {
  title: string;
  note: string;
  /** Lines under the note: what the policy words mean, one per line. */
  subnote?: string | ReadonlyArray<readonly [DealScreeningPolicy, string]>;
  firstColumn: string;
  /** Rendered between the header and the table. */
  before?: React.ReactNode;
  children: React.ReactNode;
}

const PolicyCard: React.FC<PolicyCardProps> = ({
  title,
  note,
  subnote,
  firstColumn,
  before,
  children,
}) => (
  <section
    className="overflow-hidden rounded-xl border border-gray-200 bg-white"
    aria-label={title}
  >
    <header className="border-b border-gray-100 px-[18px] py-4">
      <h2 className="text-[14px] font-semibold text-[#111111]">{title}</h2>
      <p className="mt-0.5 text-[12.5px] text-[#6B7280]">{note}</p>
      {subnote && (
        <div className="mt-1 text-[12.5px] text-[#6B7280]">
          {typeof subnote === "string" ? (
            <p>{subnote}</p>
          ) : (
            subnote.map(([policy, meaning]) => (
              <p key={policy}>
                {POLICY_LABEL[policy]}: {meaning}
              </p>
            ))
          )}
        </div>
      )}
    </header>
    {before && (
      <div className="border-b border-gray-100 px-[18px] py-3">{before}</div>
    )}
    <table className="w-full text-left">
      <thead>
        <tr className="bg-[#fafafa] text-xs text-[#5B6472]">
          <th className="px-[18px] py-2 font-semibold">{firstColumn}</th>
          <th className="px-[18px] py-2 font-semibold">How much it matters</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </section>
);

const RecipePolicyTables: React.FC<RecipePolicyTablesProps> = ({
  header,
  figures,
  figuresExtra,
  savingKeys,
  onSave,
}) => (
  <div className="grid gap-4 lg:grid-cols-2">
    <PolicyCard
      title="Company facts"
      note={`The same list on every memo. A missing one reads "Not found in the files".`}
      subnote={POLICY_MEANING}
      firstColumn="Fact"
    >
      {headerRows(header).map((row) => {
        const keys = row.fields.map((field) => field.key);
        return (
          <tr key={row.id} className="border-t border-gray-100">
            <td className="px-[18px] py-2.5">
              <p className="text-sm text-text-strong">{row.label}</p>
              {row.note && (
                <p className="text-xs text-text-muted">{row.note}</p>
              )}
            </td>
            <td className="px-[18px] py-2.5">
              <PolicySelect
                label={row.label}
                value={row.fields[0].policy}
                disabled={keys.some((key) => savingKeys.has(key))}
                onChange={(policy) =>
                  onSave(
                    {
                      header: Object.fromEntries(
                        keys.map((key) => [key, { policy }]),
                      ),
                    },
                    keys,
                  )
                }
              />
            </td>
          </tr>
        );
      })}
    </PolicyCard>

    <PolicyCard
      title="Financial figures"
      note="Read from the files. Doppler never writes a number itself."
      firstColumn="Figure"
      before={figuresExtra}
    >
      {figures
        .filter((figure) => figure.enabled && figure.printed !== false)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((figure) => {
          const { label } = figure;
          return (
            <tr key={figure.key} className="border-t border-gray-100">
              <td className="px-[18px] py-2.5">
                <p className="text-sm text-text-strong">{label}</p>
                {figure.kind === "derived" && (
                  <p className="text-xs text-text-muted">
                    Calculated from the rows above
                    {figure.formula ? ` · ${figure.formula}` : ""}
                  </p>
                )}
              </td>
              <td className="px-[18px] py-2.5">
                {figure.kind === "derived" ? (
                  <span className="text-sm text-text-muted">Calculated</span>
                ) : (
                  <PolicySelect
                    label={label}
                    value={figure.policy}
                    disabled={savingKeys.has(figure.key)}
                    onChange={(policy) =>
                      onSave({ figures: { [figure.key]: { policy } } }, [
                        figure.key,
                      ])
                    }
                  />
                )}
              </td>
            </tr>
          );
        })}
    </PolicyCard>
  </div>
);

export default RecipePolicyTables;
